/**
 * 患者の認証の API（/api/patient-auth/*。Issue #473）。ops/backoffice の authentication/authenticationApi.ts を手本にする。
 *
 * lib/http.ts（Amplify のトークンを付け、401 で Cognito の画面へ送る）は使わず、ここで fetch を呼ぶ。
 * orval の生成物からは型だけを使う（生成されたフックは lib/http.ts を通るため使わない）。
 */
import { z } from "zod";

import type {
  PatientAuthOtpAuthenticateResponse,
  PatientAuthSignInResponseOneTimePasswordSender,
} from "../../lib/api/generated/sASOnlineClinicBackendAPI.schemas";

import { SESSION_STORAGE_UNAVAILABLE_MESSAGE } from "./patientAuthMessages";
import { patientLoginPath } from "./patientLoginPath";
import {
  type PatientSession,
  clearPatientSession,
  currentPatientSession,
  savePatientSession,
} from "./patientSession";

const BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:8080/api";

export type OneTimePasswordSender = PatientAuthSignInResponseOneTimePasswordSender;

/**
 * API の失敗。status が 0 なら通信の失敗。
 * detail（利用者向けの日本語の文言）と code は problem+json の本文から取る（lib/problemMessage.ts の getProblemMessage で読める形）。
 */
export class PatientAuthApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly detail?: string;
  readonly title?: string;
  readonly cause?: unknown;

  constructor(
    status: number,
    problem: { code?: string; detail?: string; title?: string } = {},
    options?: { cause?: unknown },
  ) {
    super(
      `patient-auth API failed: status=${status} code=${problem.code ?? ""}`,
    );
    this.cause = options?.cause;
    this.name = "PatientAuthApiError";
    this.status = status;
    this.code = problem.code;
    this.detail = problem.detail;
    this.title = problem.title;
  }
}

export const isPatientAuthApiError = (
  error: unknown,
): error is PatientAuthApiError => error instanceof PatientAuthApiError;

function problemOf(
  text: string,
): {
  code?: string;
  detail?: string;
  title?: string;
} {
  try {
    const value: unknown = JSON.parse(text);
    if (typeof value !== "object" || value === null) return {};
    const { code, detail, title } = value as Record<string, unknown>;
    return {
      code: typeof code === "string" ? code : undefined,
      detail: typeof detail === "string" ? detail : undefined,
      title: typeof title === "string" ? title : undefined,
    };
  } catch {
    return {};
  }
}

async function post<T>(
  path: string,
  body: unknown,
  schema: z.ZodType<T>,
  accessToken?: string,
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  let response: Response;
  try {
    response = await fetch(`${BASE}/patient-auth/${path}`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });
  } catch (error) {
    throw new PatientAuthApiError(0, {}, { cause: error });
  }
  const text = await response.text().catch(() => "");
  if (!response.ok) {
    throw new PatientAuthApiError(response.status, problemOf(text));
  }
  let value: unknown = {};
  try {
    value = text === "" ? {} : JSON.parse(text);
  } catch (error) {
    throw new PatientAuthApiError(response.status, {}, { cause: error });
  }
  const parsed = schema.safeParse(value);
  if (!parsed.success) {
    throw new PatientAuthApiError(response.status, {}, { cause: parsed.error });
  }
  return parsed.data;
}

const empty = z.object({}).passthrough();
const oneTimeTokenSchema = z.object({ oneTimeToken: z.string().min(1) });
const tokensSchema = z.object({
  accessToken: z.string().min(1),
  refreshToken: z.string().min(1),
});
const signInSchema = z.object({
  oneTimePasswordRequired: z.boolean(),
  oneTimeToken: z.string().optional(),
  oneTimePasswordSender: z.enum(["SAS", "COGNITO"]).optional(),
  accessToken: z.string().optional(),
  refreshToken: z.string().optional(),
});
const otpResultSchema = z.object({
  requirePasswordChange: z.boolean(),
  oneTimeToken: z.string().min(1),
});

function sessionOf(tokens: {
  accessToken: string;
  refreshToken: string;
}): PatientSession {
  return { accessToken: tokens.accessToken, refreshToken: tokens.refreshToken };
}

/** 画面の側の失敗（API の応答ではない）の code。localStorage に書けずログインを終えられない。 */
export const SESSION_STORAGE_UNAVAILABLE =
  "PATIENT_SESSION_STORAGE_UNAVAILABLE";

/**
 * ログインで受け取ったトークンを保存する。書けなければ、作られたリフレッシュトークンを失効させてから投げる
 * （書けないまま進むと、ホームへ移った直後にログインの画面へ戻され、理由が分からないまま繰り返すことになるため）。
 */
async function saveSignedInSession(tokens: {
  accessToken: string;
  refreshToken: string;
}): Promise<void> {
  if (savePatientSession(sessionOf(tokens))) return;
  await revokeRefreshToken(tokens.refreshToken);
  throw new PatientAuthApiError(200, {
    code: SESSION_STORAGE_UNAVAILABLE,
    detail: SESSION_STORAGE_UNAVAILABLE_MESSAGE,
  });
}

export type SignInResult = {
  oneTimePasswordRequired: true;
  oneTimeToken: string;
  oneTimePasswordSender: OneTimePasswordSender;
} | { oneTimePasswordRequired: false };

/** 認証コードを外した患者（oneTimePasswordRequired=false）なら、受け取ったトークンをその場で保存する。 */
export async function signIn(
  loginId: string,
  password: string,
): Promise<SignInResult> {
  const result = await post("sign-in", { loginId, password }, signInSchema);
  if (result.oneTimePasswordRequired) {
    if (!result.oneTimeToken) {
      throw new PatientAuthApiError(200);
    }
    return {
      oneTimePasswordRequired: true,
      oneTimeToken: result.oneTimeToken,
      oneTimePasswordSender: result.oneTimePasswordSender ?? "SAS",
    };
  }
  const tokens = tokensSchema.safeParse(result);
  if (!tokens.success) {
    throw new PatientAuthApiError(200, {}, { cause: tokens.error });
  }
  await saveSignedInSession(tokens.data);
  return { oneTimePasswordRequired: false };
}

/** 新しいワンタイムトークンを返す（以後の段で使う）。再送したコードは常に SAS から届く。 */
export async function resendOneTimePassword(
  oneTimeToken: string,
): Promise<string> {
  return (
    await post("one-time-password/resend", { oneTimeToken }, oneTimeTokenSchema)
  ).oneTimeToken;
}

export function authenticateOneTimePassword(
  oneTimeToken: string,
  oneTimePassword: string,
): Promise<PatientAuthOtpAuthenticateResponse> {
  return post(
    "otp-authenticate",
    { oneTimeToken, oneTimePassword },
    otpResultSchema,
  );
}

export async function changeInitialPassword(
  oneTimeToken: string,
  newPassword: string,
): Promise<string> {
  return (
    await post(
      "change-initial-password",
      { oneTimeToken, newPassword },
      oneTimeTokenSchema,
    )
  ).oneTimeToken;
}

/** トークンを保存する。保存できなければ投げる（code は SESSION_STORAGE_UNAVAILABLE）。 */
export async function issueTokens(oneTimeToken: string): Promise<void> {
  const tokens = await post("issue-tokens", { oneTimeToken }, tokensSchema);
  await saveSignedInSession(tokens);
}

const refreshLockName = "sas.patient.session.refresh";

/** タブをまたいで取り替えを 1 つずつにする（Web Locks API が無いブラウザでは、このタブの中だけでまとめる）。 */
function withCrossTabLock<T>(action: () => Promise<T>): Promise<T> {
  const locks =
    typeof navigator !== "undefined" && "locks" in navigator
      ? navigator.locks
      : undefined;
  if (!locks || typeof locks.request !== "function") return action();
  return locks.request(refreshLockName, action) as Promise<T>;
}

let refreshing: Promise<PatientSession | null> | null = null;

/**
 * リフレッシュトークンで取り替え、新しいセッションを返す。
 *
 * 取り替えを受け付けない応答（400 の PATIENT_AUTH_SESSION_EXPIRED、API のフラグが OFF の 404）なら、セッションを消して null を返す。
 * 5xx・通信の失敗などはセッションを消さずに投げる（一時的な失敗でログアウトさせない）。
 * 同時に呼ばれても 1 回にまとめ、タブをまたいでも Web Locks API でなるべく 1 回にまとめる
 * （同じトークンを複数のタブが取り替えると、使われない後継がセッションの期限まで残るため）。
 */
export function refreshPatientSession(): Promise<PatientSession | null> {
  refreshing ??= doRefresh().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

async function doRefresh(): Promise<PatientSession | null> {
  const started = currentPatientSession();
  if (started === null) return null;
  return withCrossTabLock(async () => {
    const session = currentPatientSession();
    if (session === null) return null;
    // 待つ間にほかのタブが取り替えたなら、その結果を使う
    if (session.refreshToken !== started.refreshToken) return session;
    const sent = session.refreshToken;
    // 通信を待つ間に、ログアウトされたり、別のタブが先に取り替えたりしうる。
    // 結果を保存する（または消す）前に、送ったリフレッシュトークンが今も保存されているかを確かめる
    const stillCurrent = () => currentPatientSession()?.refreshToken === sent;
    try {
      const tokens = await post(
        "refresh",
        { refreshToken: sent },
        tokensSchema,
      );
      if (!stillCurrent()) return currentPatientSession();
      const next = sessionOf(tokens);
      savePatientSession(next);
      return next;
    } catch (error) {
      if (
        error instanceof PatientAuthApiError &&
        (error.status === 400 || error.status === 404)
      ) {
        if (!stillCurrent()) return currentPatientSession();
        clearPatientSession();
        return null;
      }
      throw error;
    }
  });
}

/**
 * サーバー側のリフレッシュトークンを使えなくする（同じ患者のほかの端末のログインは残る）。
 * 失敗しても投げない。保存したセッションは消さない（呼び手が、ほかの後始末を済ませてから消す）。
 */
export async function revokePatientSession(): Promise<void> {
  const session = currentPatientSession();
  if (session === null) return;
  await revokeRefreshToken(session.refreshToken);
}

async function revokeRefreshToken(refreshToken: string): Promise<void> {
  try {
    await post("sign-out", { refreshToken }, empty);
  } catch (error) {
    console.warn("patient sign-out request failed", error);
  }
}

function leaveForLogin(): void {
  clearPatientSession();
  window.location.assign(patientLoginPath("expired"));
}

const sessionExpired = () =>
  new PatientAuthApiError(401, {
    detail: "ログインの有効期限が切れました。もう一度ログインしてください。",
  });

/**
 * ログインした患者のパスワードを変える。自分のセッションのアクセストークンを付け、401 なら 1 回だけ取り替えて送り直す。
 *
 * 200 なら応答のトークンをその場で保存する（ほかの端末と、この端末の前のトークンは使えなくなっているため）。
 * PATIENT_AUTH_SESSION_EXPIRED（別の端末の変更やリセットとの競合、ログインが切れている）と、取り替えにも失敗した 401 は、
 * セッションを消してログインの画面へ移す。ほかの失敗は投げる（画面は detail をそのまま出す）。
 */
export async function changePatientPassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  const body = { currentPassword, newPassword };
  const send = (session: PatientSession) =>
    post("password", body, tokensSchema, session.accessToken);

  let tokens;
  try {
    const session = currentPatientSession();
    if (session === null) throw sessionExpired();
    try {
      tokens = await send(session);
    } catch (error) {
      if (!(error instanceof PatientAuthApiError) || error.status !== 401) {
        throw error;
      }
      const refreshed = await refreshPatientSession();
      if (refreshed === null) throw sessionExpired();
      tokens = await send(refreshed);
    }
  } catch (error) {
    if (
      error instanceof PatientAuthApiError &&
      (error.status === 401 || error.code === "PATIENT_AUTH_SESSION_EXPIRED")
    ) {
      leaveForLogin();
    }
    throw error;
  }
  savePatientSession(sessionOf(tokens));
}
